// Central theme token registry. Add a token here and both the admin editor
// and the preview injector pick it up automatically. Values are HSL triplets
// ("0 80% 45%") to match the existing tokens in src/styles.css.
export type ThemeToken = { key: string; label: string; group: string; base: string };

export const THEME_TOKENS: ThemeToken[] = [
  { key: "--primary", label: "Primary (buttons, links)", group: "Brand", base: "0 80% 45%" },
  { key: "--primary-foreground", label: "Text on primary", group: "Brand", base: "0 0% 100%" },
  { key: "--secondary", label: "Secondary (dark bars, footer)", group: "Brand", base: "220 15% 15%" },
  { key: "--secondary-foreground", label: "Text on secondary", group: "Brand", base: "0 0% 100%" },
  { key: "--accent", label: "Accent (badges, highlights)", group: "Brand", base: "40 90% 50%" },
  { key: "--accent-foreground", label: "Text on accent", group: "Brand", base: "220 20% 10%" },
  { key: "--ring", label: "Focus ring", group: "Brand", base: "0 80% 45%" },
  { key: "--background", label: "Page background", group: "Surfaces", base: "0 0% 100%" },
  { key: "--foreground", label: "Body text", group: "Surfaces", base: "220 20% 10%" },
  { key: "--card", label: "Card background", group: "Surfaces", base: "0 0% 100%" },
  { key: "--muted", label: "Muted background", group: "Surfaces", base: "220 14% 96%" },
  { key: "--muted-foreground", label: "Muted text", group: "Surfaces", base: "220 10% 40%" },
  { key: "--border", label: "Borders", group: "Surfaces", base: "220 13% 91%" },
];

export const PREVIEW_PARAM = "theme_preview";
export const PREVIEW_STORAGE_KEY = "rex-theme-preview";

export function tokensToCss(tokens: Record<string, string>): string {
  const allowed = new Set(THEME_TOKENS.map((t) => t.key));
  const body = Object.entries(tokens)
    .filter(([k, v]) => allowed.has(k) && /^[\d.\s%a-z-]+$/i.test(v))
    .map(([k, v]) => `${k}: ${v};`)
    .join(" ");
  return body ? `:root { ${body} }` : "";
}

export function hslToHex(hsl: string): string {
  const m = hsl.match(/([\d.]+)\s+([\d.]+)%\s+([\d.]+)%/);
  if (!m) return "#000000";
  const h = +m[1]!, s = +m[2]! / 100, l = +m[3]! / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
    return Math.round(c * 255).toString(16).padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

export function hexToHsl(hex: string): string {
  const v = hex.replace("#", "");
  const r = parseInt(v.slice(0, 2), 16) / 255, g = parseInt(v.slice(2, 4), 16) / 255, b = parseInt(v.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
    h *= 60;
  }
  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}
